import { Close as CloseIcon } from "@mui/icons-material";
import { CircularProgress, Dialog as MuiDialog } from "@mui/material";
import { FC } from "react";
import { motion } from "@/theme";
import { StyledButton } from "../UIComponents/StyledButton";
import { ModalTitle, ModalTitleCloseButton } from "../UIComponents/ModalTitle";
import { ModalActions } from "../UIComponents/ModalActions";
import { ModalContent } from "../UIComponents/ModalContent";
import { DialogConfig } from "./types";

type Props = {
  config: DialogConfig;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
};

/**
 * Dialog component that renders based on provided configuration.
 * Supports custom title, content, actions, and close behavior.
 */
const Dialog: FC<Props> = ({ config, open, onClose, onExited }) => {
  const {
    title,
    content,
    showCloseButton = true,
    confirmLabel = "Confirmar",
    cancelLabel = "Cancelar",
    onConfirm,
    onCancel,
    confirming = false,
    maxWidth = "sm",
  } = config;

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      slotProps={{
        transition: {
          easing: {
            enter: motion.easing.enter,
            exit: motion.easing.exit,
          },
          onExited: onExited,
        },
      }}
    >
      <ModalTitle>
        {title}
        {showCloseButton && (
          <ModalTitleCloseButton
            onClick={onClose}
            size="small"
            disabled={confirming}
          >
            <CloseIcon />
          </ModalTitleCloseButton>
        )}
      </ModalTitle>

      <ModalContent>{content}</ModalContent>

      <ModalActions>
        {onCancel !== undefined && (
          <StyledButton
            onClick={handleCancel}
            variant="outlined"
            disabled={confirming}
          >
            {cancelLabel}
          </StyledButton>
        )}
        {onConfirm !== undefined && (
          <StyledButton
            onClick={handleConfirm}
            variant="contained"
            disabled={confirming}
            startIcon={confirming ? <CircularProgress size={16} /> : null}
          >
            {confirmLabel}
          </StyledButton>
        )}
      </ModalActions>
    </MuiDialog>
  );
};

export default Dialog;
